<template>
  <div class="bg-mtg-dark-cyan py-[40px] px-[30px] rounded-[5px]">
    <div class="font-primary font-extrabold text-[20px] text-white uppercase pb-[15px]">{{ package_booking_title }}
    </div>
    <div class="font-primary font-light text-[14px] text-white pb-[30px]" v-html="package_booking_content"></div>
    <ValidationObserver ref="formObserver">
      <form v-on:submit.prevent="onSubmit">
        <ValidationProvider rules="name_required" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[15px]" v-bind:class="errors.length ? 'border-mtg-light-green' : 'border-mtg-medium-grey'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
            <input type="text" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-model="name" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_1" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
          </div>
        </ValidationProvider>
        <ValidationProvider rules="email_required|email_valid" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[15px]" v-bind:class="errors.length ? 'border-mtg-light-green' : 'border-mtg-medium-grey'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <input type="email" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-model="email" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_2" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
          </div>
        </ValidationProvider>
        <ValidationProvider rules="phone_required" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[15px]" v-bind:class="errors.length ? 'border-mtg-light-green' : 'border-mtg-medium-grey'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
            </svg>
            <input type="text" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-model="phone" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_3" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
          </div>
        </ValidationProvider>
        <ValidationProvider rules="date_required|date_valid" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[15px]">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <v-date-picker v-model="date" v-bind:model-config="modelConfig" mode="date" v-bind:masks="masks">
              <template v-slot="{ inputValue, inputEvents }">
                <input type="text" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-bind:value="inputValue" v-on="inputEvents" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_4" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
              </template>
            </v-date-picker>
          </div>
        </ValidationProvider>
        <ValidationProvider rules="ticket_required|ticket_valid" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[15px]" v-bind:class="errors.length ? 'border-mtg-light-green' : 'border-mtg-medium-grey'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
            </svg>
            <input type="number" min="1" step="1" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-model="ticket" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_5" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
          </div>
        </ValidationProvider>
        <ValidationProvider rules="message_required" v-slot="{ errors }">
          <div class="flex items-center min-h-[40px] bg-mtg-light-grey border-[1px] rounded-[5px] mb-[30px]" v-bind:class="errors.length ? 'border-mtg-light-green' : 'border-mtg-medium-grey'">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-[25px] h-[25px] text-mtg-medium-grey ml-[15px] mr-[5px]" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
            </svg>
            <input type="text" class="w-full h-full font-primary font-light text-[14px] text-mtg-medium-grey bg-mtg-light-grey border-none rounded-none focus:outline-none focus-within:outline-none" v-model="message" v-bind:placeholder="errors.length ? errors[0] : package_booking_placeholder_6" v-bind:class="errors.length ? 'placeholder-mtg-medium-blue' : 'placeholder-mtg-medium-grey'">
          </div>
        </ValidationProvider>
        <div class="flex items-center justify-center">
          <svg v-if="loading" class="animate-spin text-mtg-light-green w-[20px] h-[20px]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <button type="submit" class="inline-flex items-center justify-center text-white bg-transparent border-white py-[10px] px-[30px] border-[1px] rounded-[30px] my-[5px] mx-[10px] cursor-pointer transition-all ease-in-out duration-300 hover:text-mtg-medium-blue hover:bg-mtg-light-green hover:border-mtg-medium-blue">
            <span class="font-primary text-[15px]">{{ package_booking_label }}</span>
          </button>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>
<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate';
import { required, email, numeric } from 'vee-validate/dist/rules';
import axios from 'axios';

extend('name_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_1
});

extend('email_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_2
});

extend('email_valid', {
  ...email,
  message: window.package_post_script_data.package_booking_error_3
});

extend('phone_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_4
});

extend('date_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_5
});

extend('date_valid', {
  ...required,
  message: window.package_post_script_data.package_booking_error_6
});

extend('ticket_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_7
});

extend('ticket_valid', {
  ...numeric,
  message: window.package_post_script_data.package_booking_error_8
});

extend('message_required', {
  ...required,
  message: window.package_post_script_data.package_booking_error_9
});

export default {
  name: 'PackageBookingForm',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data () {
    return {
      wp_ajax: window.package_post_script_data.wp_ajax,
      wp_nonce: window.package_post_script_data.wp_nonce,
      wp_action: 'submit_package_booking_form',
      recaptcha_token: '',
      loading: false,
      package_booking_title: window.package_post_script_data.package_booking_title,
      package_booking_content: window.package_post_script_data.package_booking_content,
      package_booking_placeholder_1: window.package_post_script_data.package_booking_placeholder_1,
      package_booking_placeholder_2: window.package_post_script_data.package_booking_placeholder_2,
      package_booking_placeholder_3: window.package_post_script_data.package_booking_placeholder_3,
      package_booking_placeholder_4: window.package_post_script_data.package_booking_placeholder_4,
      package_booking_placeholder_5: window.package_post_script_data.package_booking_placeholder_5,
      package_booking_placeholder_6: window.package_post_script_data.package_booking_placeholder_6,
      package_booking_label: window.package_post_script_data.package_booking_label,
      package_booking_submission_succeeded: window.package_post_script_data.package_booking_submission_succeeded,
      package_booking_submission_failed: window.package_post_script_data.package_booking_submission_failed,
      package_post_title: window.package_post_script_data.package_post_title,
      package_title: window.package_post_script_data.package_title,
      name: '',
      email: '',
      phone: '',
      date: '',
      // date: (new Date()).toLocaleDateString('en-GB'),
      modelConfig: {
        type: 'string',
        mask: 'DD-MM-YYYY',
      },
      masks: {
        input: 'DD-MM-YYYY',
      },
      ticket: '',
      message: '',
    };
  },
  methods: {
    async onSubmit () {
      await this.$recaptchaLoaded();
      this.recaptcha_token = await this.$recaptcha(this.wp_action);
      this.$refs.formObserver.validate().then(success => {
        if (success) {
          this.loading = true;
          let formData = new FormData();
          formData.append('wp_nonce', this.wp_nonce);
          formData.append('action', this.wp_action);
          formData.append('recaptcha_token', this.recaptcha_token);
          formData.append('name', this.name);
          formData.append('email', this.email);
          formData.append('phone', this.phone);
          formData.append('date', this.date);
          formData.append('ticket', this.ticket);
          formData.append('message', this.message);
          formData.append('package_post_title', this.package_post_title);
          formData.append('package_title', this.package_title);
          axios({
            method: 'POST',
            url: this.wp_ajax,
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            data: formData,
          }).then(() => {
            this.$swal('Sent', this.package_booking_submission_succeeded, 'success');
            this.name = this.email = this.phone = this.date = this.ticket = this.message = '';
            this.$nextTick(() => {
              this.$refs.formObserver.reset();
            });
            this.loading = false;
          }).catch(() => {
            this.$swal('Error', this.package_booking_submission_failed, 'error');
            this.loading = false;
          });
        }
      });
      this.loading = false;
    }
  }
};
</script>
<style scoped>
</style>
